import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Car, ChevronRight, Info } from "lucide-react";
import { AddressTimelineClickable } from "@/components/address-timeline-clickable";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SolicitacaoService } from "@/services/solicitacao.service";
import { USER_STORAGE_KEY } from "@/config/base";

export const Route = createFileRoute("/app/entregas/checkout")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      type: (search.type as "send" | "receive") || "send",
      originLat: search.originLat as number,
      originLng: search.originLng as number,
      originAddress: search.originAddress as string,
      destinationLat: search.destinationLat as number,
      destinationLng: search.destinationLng as number,
      destinationAddress: search.destinationAddress as string,
      destinationName: search.destinationName as string | undefined,
      destinationPhone: search.destinationPhone as string | undefined,
      favoritoId: search.favoritoId as string | undefined,
      snapshotId: search.snapshotId as string | undefined,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();

  const [paymentMethod, setPaymentMethod] = useState<"pix" | "credito" | "debito">("pix");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showProhibited, setShowProhibited] = useState(false);
  const [estimatedPrice] = useState(218.92); // TODO: Calculate from API

  // Get user info
  const userStr = localStorage.getItem(USER_STORAGE_KEY);
  const user = userStr ? JSON.parse(userStr) : null;

  const [originInfo] = useState({
    name: user?.name || "Você",
    address: searchParams.originAddress,
    phone: user?.phone,
    deliveryOption: "Encontre na calçada",
    instructions: "",
  });

  const [destinationInfo] = useState({
    name: searchParams.destinationName || "",
    address: searchParams.destinationAddress,
    phone: searchParams.destinationPhone || "",
    deliveryOption: "Encontre na calçada",
    instructions: "",
  });

  const handleOriginClick = () => {
    // Open sheet to edit origin info
    console.log("Edit origin");
  };

  const handleDestinationClick = () => {
    // Open sheet to edit destination info
    console.log("Edit destination");
  };

  const handleConfirmDelivery = async () => {
    try {
      setIsSubmitting(true);

      const deliveryData = {
        tipo: searchParams.type === "send" ? "ENVIO" : "RETIRADA",

        // Origin
        origemLatitude: searchParams.originLat,
        origemLongitude: searchParams.originLng,
        origemRua: searchParams.originAddress,
        origemNomeContato: originInfo.name,
        origemTelefone: originInfo.phone,
        origemInstrucoes: originInfo.instructions,

        // Destination
        destinoLatitude: searchParams.destinationLat,
        destinoLongitude: searchParams.destinationLng,
        destinoRua: searchParams.destinationAddress,
        destinoNomeContato: destinationInfo.name,
        destinoTelefone: destinationInfo.phone,
        destinoInstrucoes: destinationInfo.instructions,

        // Payment
        formaPagamento: paymentMethod.toUpperCase(),

        // IDs if using saved addresses
        ...(searchParams.favoritoId && { destinoFavoritoId: parseInt(searchParams.favoritoId) }),
        ...(searchParams.snapshotId && { destinoId: parseInt(searchParams.snapshotId) }),
      };

      const result = await SolicitacaoService.create(deliveryData as any);

      // Navigate to tracking page
      navigate({
        to: "/app/entregas/$entregaId/acompanhar",
        params: { entregaId: result.id.toString() },
      });
    } catch (error) {
      console.error("Error creating delivery:", error);
      alert("Erro ao criar entrega. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate({ to: "/app/entregas/confirm-origin", search: searchParams })}
          className="p-1 hover:bg-slate-100 rounded-full transition-colors"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold">Informações da entrega</h1>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-24">
        {/* Address Timeline */}
        <div className="bg-white border-b border-slate-200 mb-4">
          <AddressTimelineClickable
            origin={originInfo}
            destination={destinationInfo}
            onOriginClick={handleOriginClick}
            onDestinationClick={handleDestinationClick}
          />
        </div>

        {/* Delivery Type */}
        <div className="bg-white border-b border-slate-200 px-4 py-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="bg-slate-100 rounded-xl p-3">
              <Car className="h-6 w-6 text-slate-700" />
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-slate-900 mb-1">
                Envio de itens
              </p>
              <p className="text-sm text-slate-600">
                Tudo cabe no porta-malas
              </p>
              <p className="text-xs text-slate-500 mt-1">
                120 x 80 x 60 cm · Até 30 kg
              </p>
            </div>
          </div>
        </div>

        {/* Estimated Time Preview */}
        <div className="bg-white border-b border-slate-200 px-4 py-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Previsão de chegada</p>
              <p className="text-base font-semibold text-slate-900">
                Hoje, 16:44
              </p>
            </div>
            <p className="text-lg font-bold text-primary">
              R$ {estimatedPrice.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white border-b border-slate-200 px-4 py-4 mb-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Pagamento</h3>
          <div className="space-y-2">
            <button
              onClick={() => setPaymentMethod("pix")}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                paymentMethod === "pix"
                  ? "border-primary bg-primary/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span className="text-sm font-medium">PIX</span>
              {paymentMethod === "pix" && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
              )}
            </button>
            <button
              onClick={() => setPaymentMethod("credito")}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                paymentMethod === "credito"
                  ? "border-primary bg-primary/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span className="text-sm font-medium">Cartão de Crédito</span>
              {paymentMethod === "credito" && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
              )}
            </button>
            <button
              onClick={() => setPaymentMethod("debito")}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                paymentMethod === "debito"
                  ? "border-primary bg-primary/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span className="text-sm font-medium">Cartão de Débito</span>
              {paymentMethod === "debito" && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Instructions and Prohibited Items */}
        <div className="bg-white border-b border-slate-200">
          <button
            onClick={() => setShowInstructions(true)}
            className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-slate-600" />
              <span className="text-sm font-medium text-slate-900">
                Confira as instruções do item
              </span>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </button>

          <button
            onClick={() => setShowProhibited(true)}
            className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors border-t border-slate-100"
          >
            <span className="text-sm font-medium text-slate-900">
              Ver itens proibidos
            </span>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Terms */}
        <div className="px-4 py-4">
          <p className="text-xs text-slate-600 text-center">
            A Uber não oferece seguro para itens. Ao tocar em "Confirmar
            pedido", você concorda com os{" "}
            <a href="#" className="text-primary font-medium">
              Termos e Condições
            </a>
            .
          </p>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="bg-white border-t border-slate-200 px-4 py-4">
        <Button
          onClick={handleConfirmDelivery}
          disabled={isSubmitting || !destinationInfo.name || !destinationInfo.phone}
          className="w-full bg-primary hover:bg-primary/90 h-12 text-base font-semibold"
        >
          {isSubmitting ? "Confirmando..." : `Confirmar · R$ ${estimatedPrice.toFixed(2)}`}
        </Button>
      </div>

      {/* Instructions Sheet */}
      <Sheet open={showInstructions} onOpenChange={setShowInstructions}>
        <SheetContent side="bottom" className="h-[80vh]">
          <SheetHeader>
            <SheetTitle>Instruções do item</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <p className="text-sm text-slate-600">
              Para uma entrega tranquila, confira se o pacote atende aos
              seguintes requisitos:
            </p>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• Valor de até R$ 5000 para viagens com seguro do item ou até R$ 500 para viagens sem seguro do item</li>
              <li>• Devidamente embalado e pronto para retirada</li>
            </ul>
          </div>
        </SheetContent>
      </Sheet>

      {/* Prohibited Items Sheet */}
      <Sheet open={showProhibited} onOpenChange={setShowProhibited}>
        <SheetContent side="bottom" className="h-[80vh]">
          <SheetHeader>
            <SheetTitle>Itens proibidos</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-4">
              Bebidas alcoólicas, medicamentos, drogas, armas de fogo, itens de
              valor emocional insubstituível, perigosos ou ilegais são
              proibidos. O envio de itens pelo app da Uber deve estar em
              conformidade com as políticas da Uber e com as leis e os
              regulamentos vigentes. As violações podem ser denunciadas às
              autoridades, e o usuário poderá perder o acesso ao app.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
